import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { toast } from 'sonner'
import { Loader2, Calendar, ExternalLink } from 'lucide-react'
import { z } from 'zod'

const trialFormSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(50, "First name must be less than 50 characters"),
  lastName: z.string().trim().min(1, "Last name is required").max(50, "Last name must be less than 50 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  role: z.string().min(1, "Role is required"),
  district: z.string().trim().min(1, "District is required").max(100, "District name must be less than 100 characters"),
  state: z.string().min(1, "State is required"),
  demoScheduled: z.boolean().refine(val => val === true, "You must schedule a demo to proceed")
})

const DEMO_CALENDAR_URL = "https://calendar.app.google/KomoKDTgSAVyqw1x5"

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
]

const ROLES = [
  'Teacher',
  'Principal',
  'Assistant Principal',
  'Superintendent',
  'Curriculum Director',
  'Professional Development Coordinator',
  'Instructional Coach',
  'Department Head',
  'Other'
]

export function TrialForm() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    district: '',
    state: '',
    demoScheduled: false
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setErrors({})

    try {
      // Validate form data
      const validatedData = trialFormSchema.parse(formData)
      
      // For now, just show success message without database save
      // TODO: Create trial_requests table when needed
      toast.success('Trial request received! We\'ll be in touch soon.')
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        district: '',
        state: '',
        demoScheduled: false
      })
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {}
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message
          }
        })
        setErrors(fieldErrors)
        toast.error('Please fix the errors below')
      } else {
        toast.error(error.message || 'Failed to submit trial request')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>Request Trial Access</CardTitle>
        <CardDescription>
          Fill out this form to request trial access to Edugogy. <strong>A demo call is required</strong> before we can activate your trial.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Alert className="mb-6 border-blue-200 bg-blue-50">
          <Calendar className="h-4 w-4" />
          <AlertDescription>
            <strong>Step 1:</strong> Schedule your required demo call first, then complete this form.
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2 w-full"
              onClick={() => window.open(DEMO_CALENDAR_URL, '_blank')}
            >
              <Calendar className="h-4 w-4 mr-2" />
              Schedule Demo Call
              <ExternalLink className="h-4 w-4 ml-2" />
            </Button>
          </AlertDescription>
        </Alert>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                required
                placeholder="John"
                className={errors.firstName ? "border-red-500" : ""}
              />
              {errors.firstName && <p className="text-sm text-red-500">{errors.firstName}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                required
                placeholder="Doe"
                className={errors.lastName ? "border-red-500" : ""}
              />
              {errors.lastName && <p className="text-sm text-red-500">{errors.lastName}</p>}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              required
              placeholder="john.doe@district.edu"
              className={errors.email ? "border-red-500" : ""}
            />
            {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Select value={formData.role} onValueChange={(value) => handleInputChange('role', value)}>
              <SelectTrigger className={errors.role ? "border-red-500" : ""}>
                <SelectValue placeholder="Select your role" />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((role) => (
                  <SelectItem key={role} value={role}>
                    {role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.role && <p className="text-sm text-red-500">{errors.role}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="district">District</Label>
            <Input
              id="district"
              value={formData.district}
              onChange={(e) => handleInputChange('district', e.target.value)}
              required
              placeholder="Your School District"
              className={errors.district ? "border-red-500" : ""}
            />
            {errors.district && <p className="text-sm text-red-500">{errors.district}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="state">State</Label>
            <Select value={formData.state} onValueChange={(value) => handleInputChange('state', value)}>
              <SelectTrigger className={errors.state ? "border-red-500" : ""}>
                <SelectValue placeholder="Select your state" />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map((state) => (
                  <SelectItem key={state} value={state}>
                    {state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.state && <p className="text-sm text-red-500">{errors.state}</p>}
          </div>

          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="demoScheduled"
                checked={formData.demoScheduled}
                onCheckedChange={(checked) => handleInputChange('demoScheduled', !!checked)}
              />
              <Label 
                htmlFor="demoScheduled" 
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                I have scheduled a demo call using the link above
              </Label>
            </div>
            {errors.demoScheduled && <p className="text-sm text-red-500">{errors.demoScheduled}</p>}
          </div>

          <Button type="submit" className="w-full" disabled={loading || !formData.demoScheduled}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Request Trial Access
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}